import { defineStore } from 'pinia'
import TextCell from '@/onenetwork/components/datatable/TextCell.vue'
import ThumbCell from '@/onenetwork/components/datatable/ThumbCell.vue'
import DateTimeCell from '@/onenetwork/components/datatable/DateTimeCell.vue'
import { uuid } from 'vue3-uuid'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import AccountAPI from '@/modules/account/constants/account.api.uri.const.ts'
import { formatTableDataBo } from '../helper/table'
import { buildFilterObject } from '@/utils/filter'
import PhoneCell from '@/onenetwork/components/datatable/PhoneCell.vue'
import { markRaw } from 'vue'
import TagCell from '@/onenetwork/components/datatable/TagCell.vue'

export interface tabItem {
  id: string,
  label: string,
  active: boolean,
}

export enum AccountBOActionTypes {
  FILTERING = 'FILTERING',
  PENDING= "PENDING",
  FETCHING= "FETCHING",
  FETCH_SUCCESS = "FETCH_SUCCESS",
  FETCH_FAILURE = "FETCH_FAILURE",
}

interface AccountListStore {
  action_type: AccountBOActionTypes,
  action_mess: string,
  uuid: string,
  data: Object,
  filter: Object,
  pageSize: number,
  page: number,
  tabs: Array<any>,
  tableConfig: any,
  selectedIds: Array<string|number>,
}

export const useAccountListStore = defineStore('account.bo', {
  state: (): AccountListStore => ({
    action_type: AccountBOActionTypes.PENDING,
    action_mess: "",
    uuid: '',
    data: {
      list: [],
      totalRecords: 0,
      totalPage: 0,
    },
    filter: [
      {
        key: 'code',
        type: 'text',
        label: 'Mã tài khoản',
        placeholder: 'Nhập mã tài khoản',
        value: '',
      },
      {
        key: 'keyword',
        type: 'taginput',
        label: 'Lọc theo từ khoá',
        placeholder: 'Nhập từ khoá',
        value: '',
      },

      {
        key: 'registerDate',
        type: 'date-range',
        label: 'Lọc theo ngày tạo',
        placeholder: 'Chọn ngày tạo',
        value: '',
      },
    ],
    pageSize: 10,
    page: 1,
    tabs: [
      {
        id: 'list',
        label: 'Danh sách tài khoản',
        active: true,
      }
    ],
    tableConfig: {
      canShowHide: true,
      columns: {
        code: { label: 'CODE', component: markRaw(TextCell), visible: true, sort: false, config: {} },
        avatar: { label: 'Ảnh', component: markRaw(ThumbCell), visible: true, sort: false, config: {width: '40px', height: '40px', borderRadius: '4px'} },
        phone: { label: 'SDT', component: markRaw(PhoneCell), visible: true, sort: false, config: {} },
        email: { label: 'Email', component: markRaw(TextCell), visible: true, sort: false, config: {} },
        full_name: { label: 'Tên hiển thị', component: markRaw(TextCell), visible: true, sort: false, config: {} },
        status: { label: 'Trạng thái', component: markRaw(TagCell), visible: true, sort: false, config: {} },
        perms: { label: 'Quyền hạn', component: markRaw(TextCell), visible: true, sort: false, config: {} },
        created_by: { label: 'Người tạo', component: markRaw(TextCell), visible: true, sort: false, config: {} },
        created_at: { label: 'Ngày tạo', component: markRaw(DateTimeCell), visible: true, sort: 'desc', config: {} },
      }
    },
    selectedIds: []
  }),
  getters: {
    sortFilter: state => {
      const sort: any = []
      if(Object.keys(state.tableConfig?.columns).length>0){
        Object.keys(state.tableConfig?.columns).forEach(col => {
          if(state.tableConfig.columns[col].sort) {
            sort.push([col, state.tableConfig.columns[col].sort])
          }
        })
      }
      return sort
    }
  },
  actions: {
    //TAB ACTION
    changeTab (targetId: string) {

      for (const tab of this.tabs) {
        tab.active = (tab.id === targetId)
      }
    },
    closeTab (targetId: string) {
      const index = this.tabs.findIndex(tab => tab.id === targetId)
      if (index !== -1) {
        this.tabs.splice(index, 1)
      }
    },
    closeAllTab () {
      this.tabs = [{
        id: 'list',
        label: 'Danh sách tài khoản',
        active: true,
      }]
    },
    newTab (item: tabItem) {
      const exists = this.tabs.some(tab => tab.id === item.id)
      if (!exists) {
        this.tabs.push(item)
      }
      this.changeTab(item.id)
    },

    //FILTER ACTION
    setFilter (filter: any) {
      this.filter = filter
      this.page = 1
      this.action_type = AccountBOActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    resetFilter () {
      this.filter = this.filter.map(item => ({...item, value: ''}))
      this.page = 1
      this.action_type = AccountBOActionTypes.FILTERING
      this.uuid = uuid.v4()
    },

    //PAGE ACTION
    changePage (page: number) {
      this.page = page
      this.action_type = AccountBOActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    changePageSize (size: number) {
      this.pageSize = size
      this.action_type = AccountBOActionTypes.FILTERING
      this.uuid = uuid.v4()
    },

    //TABLE CONFIG ACTION
    toggleColumnVisibility(columnCode: string, visible: boolean) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].visible = visible
      } else {
        console.warn(`Column "${columnCode}" không tồn tại trong tableConfig`)
      }
    },
    toggleSort(columnCode: string, sort: string) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].sort = sort
        this.action_type = AccountBOActionTypes.FILTERING
        this.uuid = uuid.v4()
      } else {
        console.warn(`Column "${columnCode}" không tồn tại trong tableConfig`)
      }
    },
    setSelectedRow(ids: Array<string|number>) {
      this.selectedIds = ids
    },
    async deleteSelectedRow() {
      const res = await APIClient.delete(AccountAPI.bo.bulk_delete, {
        data: {
          account_ids: this.selectedIds
        }
      })
      if(res.status === 200) {
        this.selectedIds = []
        this.fetchList()
        return true
      }
      return false
    },

    //DATA ACTION
    async fetchList(){
      return new Promise(async (resolve, reject) => {
        try{
          this.action_type = AccountBOActionTypes.FETCHING
          const sort = this.sortFilter

          const pagination = {
            page: this.page,
            page_size: this.pageSize,
            sort: sort
          };

          const filter = buildFilterObject(this.filter, pagination);

          const res: any = await APIClient.get(AccountAPI.bo.list, {
            params: filter
          })
          const { data } = res.data;
          this.data = {
            list: formatTableDataBo(data.data) || [],
            totalRecords: data.total ?? 0,
            totalPage: data.last_page ?? 0
          }
          this.action_type = AccountBOActionTypes.FETCH_SUCCESS
          resolve(res)
        } catch (e) {
          this.action_type = AccountBOActionTypes.FETCH_FAILURE
          reject(e)
        }
      })
    }
  }
})
